import Button from '@atlaskit/button/new';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import React, { useCallback, useState } from 'react';

type ConfirmationModalWrapperProps = {
	title: string;
	confirmBody: React.ReactNode;
	onConfirm: () => void;
	onClose: () => void;
};

const ConfirmationModalWrapper = ({
	title,
	confirmBody,
	isOpen,
	onConfirm,
	onClose,
}: ConfirmationModalWrapperProps & { isOpen: boolean }) => {
	return (
		<ModalTransition>
			{isOpen && (
				<ConfirmationModal
					title={title}
					confirmBody={confirmBody}
					onConfirm={onConfirm}
					onClose={onClose}
				/>
			)}
		</ModalTransition>
	);
};

const ConfirmationModal = ({
	title,
	confirmBody,
	onConfirm,
	onClose,
}: ConfirmationModalWrapperProps) => {
	return (
		<Modal onClose={onClose}>
			<ModalHeader hasCloseButton>
				<ModalTitle appearance="danger">{title}</ModalTitle>
			</ModalHeader>
			<ModalBody>{confirmBody}</ModalBody>
			<ModalFooter>
				<Button appearance="subtle" onClick={onClose} testId="confirmation-modal-cancel">
					{/* eslint-disable-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
					Cancel
				</Button>
				<Button
					appearance="danger"
					onClick={onConfirm}
					testId="confirmation-modal-confirm"
					interactionName="boysenberry-complete-flow"
				>
					{/* eslint-disable-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
					Confirm
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export const useConfirmationModal = ({
	onConfirm,
	title,
	confirmBody,
}: {
	onConfirm: () => void;
	title: string;
	confirmBody: React.ReactNode;
}) => {
	const [isOpen, setIsOpen] = useState(false);

	const openConfirmationModal = useCallback(() => {
		setIsOpen(true);
	}, []);

	const closeConfirmationModal = useCallback(() => {
		setIsOpen(false);
	}, []);

	const handleConfirm = useCallback(() => {
		onConfirm();
		closeConfirmationModal();
	}, [onConfirm, closeConfirmationModal]);

	return {
		openConfirmationModal,
		closeConfirmationModal,
		ConfirmationModal: (
			<ConfirmationModalWrapper
				title={title}
				confirmBody={confirmBody}
				isOpen={isOpen}
				onConfirm={handleConfirm}
				onClose={closeConfirmationModal}
			/>
		),
	};
};
